import { z } from 'zod';

export const USER_NAVIGATION_BUILT_IN_WORKSPACES = [
    'all',
    'core',
    'iso27001',
    'training',
    'scf',
    'kvkk',
    'gdpr',
    'bcm',
    'bigdes',
    'risk',
    'asset',
    'audit',
    'ticketing',
    'document',
    'incident',
    'directory',
];

function hasUnsafeRouteCharacter(value) {
    return [...value].some((character) => {
        const code = character.charCodeAt(0);
        return /\s/.test(character) || code < 32 || code === 127;
    });
}

export const UserNavigationPageUrlSchema = z.string()
    .trim()
    .min(1)
    .max(256)
    .refine(
        (value) => value.startsWith('/')
            && !value.startsWith('//')
            && !value.includes('\\')
            && !hasUnsafeRouteCharacter(value),
        'Page URL must be a local application route',
    );

export const UserNavigationCustomWorkspaceIdSchema = z.string()
    .trim()
    .max(128)
    .regex(/^custom:[A-Za-z0-9_-]+$/, 'Invalid custom workspace id');

const uniquePageUrls = (maximum) => z.array(UserNavigationPageUrlSchema)
    .max(maximum)
    .transform((values) => [...new Set(values)]);

export const UserNavigationCustomWorkspaceSchema = z.object({
    id: UserNavigationCustomWorkspaceIdSchema,
    name: z.string().trim().min(1).max(48),
    pageUrls: uniquePageUrls(100).refine((values) => values.length > 0, {
        message: 'Custom workspace must contain at least one page',
    }),
}).strict();

export const UpdateUserNavigationPreferenceSchema = z.object({
    activeWorkspace: z.union([
        z.enum(USER_NAVIGATION_BUILT_IN_WORKSPACES),
        UserNavigationCustomWorkspaceIdSchema,
    ]),
    favoritePageUrls: uniquePageUrls(100),
    customWorkspaces: z.array(UserNavigationCustomWorkspaceSchema).max(25),
}).strict().superRefine((value, ctx) => {
    const workspaceIds = value.customWorkspaces.map(({ id }) => id);
    if (new Set(workspaceIds).size !== workspaceIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customWorkspaces'],
            message: 'Custom workspace ids must be unique',
        });
    }
    if (
        value.activeWorkspace.startsWith('custom:')
        && !workspaceIds.includes(value.activeWorkspace)
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['activeWorkspace'],
            message: 'Active custom workspace must exist',
        });
    }
});
